import React, { useRef } from "react";

import MenuIcon from "@mui/icons-material/Menu";
import { Box, Stack } from "@mui/material";
import { AsyncContentMui } from "components/Async-content/AsyncContentMui";
import { Menu } from "components/popups";
import Highcharts from "highcharts";
import HighchartsReact, { HighchartsReactRefObject } from "highcharts-react-official";
import exportData from "highcharts/modules/export-data";
import exporting from "highcharts/modules/exporting";
import fullscreen from "highcharts/modules/full-screen";
import offlineExporting from "highcharts/modules/offline-exporting";
import { IDispersionBucket } from "store/types";

exporting(Highcharts);
offlineExporting(Highcharts);
exportData(Highcharts);
fullscreen(Highcharts);

interface DispersionChartProps {
  data: IDispersionBucket[] | null;
  loading: boolean;
}

const BAR_COLOR = "#4690F5";

export const DispersionChart: React.FC<DispersionChartProps> = ({ data, loading }) => {
  const chartRef = useRef<HighchartsReactRefObject>(null);

  const categories = data?.map((b) => b.label) ?? [];
  const counts = data?.map((b) => b.count) ?? [];

  const handleExport = (type: string) => {
    (chartRef.current?.chart as any)?.exportChartLocal({ type });
  };

  const options: Highcharts.Options = {
    chart: {
      type: "column",
      height: 285,
      borderRadius: 16,
      marginTop: 30
    },
    title: {
      text: "Return Dispersion",
      align: "left",
      style: {
        fontWeight: "500",
        fontSize: "18px",
        fontFamily: "Roboto",
        color: "#071F35"
      }
    },
    credits: { enabled: false },
    exporting: { enabled: false },
    navigation: { buttonOptions: { enabled: false } },
    xAxis: {
      categories,
      lineWidth: 0,
      tickWidth: 0,
      labels: {
        style: {
          color: "#496682",
          fontSize: "12px"
        }
      }
    },
    yAxis: {
      title: { text: "Holdings" },
      gridLineColor: "#ECF1F8",
      lineWidth: 0,
      tickWidth: 0,
      allowDecimals: false,
      labels: {
        style: {
          color: "#496682",
          fontSize: "12px"
        }
      }
    },
    tooltip: {
      useHTML: true,
      outside: true,
      formatter: function () {
        const index = this.point.index;
        const bucket = data?.[index];
        if (!bucket) return `<b>${this.x}</b><br/>Count: ${this.y}`;

        const maxVisible = 10;
        const holdingsToShow = bucket.holdings.slice(0, maxVisible);
        const remaining = bucket.holdings.length - maxVisible;

        const holdingsList = holdingsToShow
          .map(
            (h) =>
              `<div style="padding:2px 0;font-size:12px;color:#071F35;display:flex;align-items:center;gap:4px;"><span style="max-width:160px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;display:inline-block;">${h.name}</span> <span style="color:#496682;white-space:nowrap;">(${h.ratio.toFixed(2)}x)</span></div>`
          )
          .join("");

        const moreText =
          remaining > 0 ? `<div style="padding:2px 0;font-size:11px;color:#92A7BC;">+${remaining} more</div>` : "";

        return `<div style="min-width:160px;max-width:260px;max-height:300px;overflow:auto;padding:4px;">
          <div style="font-weight:500;font-size:13px;color:#071F35;margin-bottom:4px;">${bucket.label}</div>
          <div style="font-size:12px;color:#496682;margin-bottom:6px;">${bucket.count} holding${bucket.count !== 1 ? "s" : ""}</div>
          ${holdingsList}${moreText}
        </div>`;
      }
    },
    legend: { enabled: false },
    plotOptions: {
      column: {
        borderRadius: 2,
        color: BAR_COLOR,
        pointPadding: 0.1,
        groupPadding: 0.15
      }
    },
    series: [
      {
        name: "Holdings",
        type: "column",
        data: counts
      }
    ],
    colors: [BAR_COLOR]
  };

  return (
    <Box
      className="chart"
      sx={{
        flexGrow: 1,
        position: "relative",
        borderRadius: "8px",
        backgroundColor: "#ffffff",
        boxShadow: "0 1px 2px 0 #1212171a",
        "&:hover .chart-option": {
          opacity: "1 !important"
        }
      }}
    >
      <AsyncContentMui isLoading={loading} boxProps={{ sx: { height: 285 } }}>
        <HighchartsReact highcharts={Highcharts} ref={chartRef} options={options} />
        <Stack
          className="menu-trigger-blocks"
          sx={{
            position: "absolute",
            top: "10px",
            right: "10px",
            flexDirection: "row",
            gap: 1
          }}
        >
          <Menu
            iconButtonProps={{ className: "chart-option", sx: { opacity: 0, transition: "opacity 0.3s linear" } }}
            keepTriggerEnabled
            icon={<MenuIcon />}
            options={[
              {
                title: "View in full screen",
                onClick: () => (chartRef.current?.chart as any).fullscreen.toggle()
              },
              {
                title: "Print chart",
                onClick: () => (chartRef.current?.chart as any).print(),
                hasLine: true
              },
              {
                title: "Download PNG image",
                onClick: () => handleExport("image/png")
              },
              {
                title: "Download JPEG image",
                onClick: () => handleExport("image/jpeg")
              },
              {
                title: "Download PDF Document",
                onClick: () => handleExport("application/pdf")
              },
              {
                title: "Download SVG vector image",
                onClick: () => handleExport("image/svg+xml"),
                hasLine: true
              },
              {
                title: "Download CSV",
                onClick: () => (chartRef.current?.chart as any).downloadCSV()
              },
              {
                title: "Download XLS",
                onClick: () => (chartRef.current?.chart as any).downloadXLS()
              }
            ]}
          />
        </Stack>
      </AsyncContentMui>
    </Box>
  );
};
